import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  IoCreateOutline,
  IoLocationOutline, IoCalendarOutline, IoBriefcaseOutline,
  IoImageOutline, IoIdCardOutline
} from "react-icons/io5";
import SidebarLayout from "../../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../../components/Layouts/PageSubHeader";
import sidebar from "../../../utils/sidebar";
import { AWS_S3_URL } from "../../../../../utils/constants";
import "./styles.css";
import { Card, CardBody, CardHeader, CardTitle, Spinner } from "reactstrap";
import { get } from "../../../../../utils/helpers/api";
import OverviewTab from "./Tabs/Overview";
import ActivitiesTab from "./Tabs/Activities";
import ResumeTab from "./Tabs/Resume";
import LeaveTab from "./Tabs/Leave";
import FilesTab from "./Tabs/Files";

function ViewEmployeePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const { state } = useLocation();
  const [loading, setLoading] = useState(true);
  const [emp, setEmp] = useState(state?.employeeData || {});
  const [filesCount, setFilesCount] = useState(state?.employeeData?.files?.length ?? null);
  const [activeTab, setActiveTab] = useState("overview");

  const TABS = [
    { id: "overview", label: t(`hr.employee.overview`) },
    { id: "activities", label: t(`hr.employee.activities`) },
    { id: "resume", label: t(`hr.employee.cv`) },
    { id: "leave", label: t(`hr.employee.leave`) },
    { id: "files", label: t(`hr.employee.files`) },
  ];

  const fullName =
    [emp.firstname, emp.middlename, emp.lastname].filter(Boolean).join(" ") || "";
  const initials = fullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("");

  // Placeholder-friendly getters
  const location = [emp.city, emp.country].filter(Boolean).join(", ") || "";

  const getEmployeeData = async (displayLoading = true) => {
    if (displayLoading) setLoading(true);

    const data = await get('/hr/employees/view/' + id);
    if (data?.success) {
      setEmp(data.employee);
      setFilesCount(data.files?.total ?? 0);
    } else {
      console.error("Failed to fetch employee data:", data?.message || "Unknown error");
    }

    if (displayLoading) setLoading(false);
  }

  useEffect(() => {
    if (!state?.employeeData) {
      getEmployeeData();
    } else {
      setLoading(false);
    }
  }, [id, state]);

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={["Employees", fullName]}
          onBack={() => navigate("/hr/employees")}
        />

        <div className="subheader-page-content scrollable">
          {loading ? 
            <Spinner color="primary" className="d-block mx-auto my-4" />
          :
            <div className="emp-profile">
              {/* ---------------- Top card ---------------- */}
              <div className="emp-topcard">
                <div className="emp-cover"></div>

                <div className="emp-header">
                  <div className="emp-avatar-wrap" style={{ position: "relative" }}>
                    {emp.profile_picture_path ? (
                      <img
                        className="emp-avatar"
                        src={AWS_S3_URL + emp.profile_picture_path}
                      />
                    ) : (
                      <div className="emp-avatar-fallback">{initials}</div>
                    )}
                  </div>

                  <div className="emp-header-body">
                    <div className="emp-header-top">
                      <div>
                        <div className="emp-name-row">
                          <h1 className="emp-name">{fullName}</h1>
                          <span className="emp-pill is-active">
                            <span className="emp-pill-dot" /> { t(`hr.employee.active`) }
                          </span>
                        </div>

                        <p className="emp-headline">
                          {emp.job_title}
                          <span className="sep">·</span>
                          <span className="muted">{emp.department} Department</span>
                        </p>

                        <div className="emp-meta">
                          <span className="emp-meta-item">
                            <IoIdCardOutline size={16} /> {emp.id}
                          </span>
                          <span className="emp-meta-item">
                            <IoLocationOutline size={16} /> {location}
                          </span>
                          <span className="emp-meta-item">
                            <IoBriefcaseOutline size={16} /> Full-time
                          </span>
                          <span className="emp-meta-item">
                            <IoCalendarOutline size={16} /> Joined {emp.contract_start_date}
                          </span>
                        </div>
                      </div>

                      <div className="emp-actions">
                        <button
                          type="button"
                          className="emp-btn is-primary"
                          onClick={() =>
                            navigate("/hr/employees/edit/" + (emp.id ?? id), {
                              state: { employeeData: emp },
                            })
                          }
                        >
                          <IoCreateOutline size={16} /> { t(`hr.employee.edit_profile`) }
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="emp-tabs">
                  {TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      className={"emp-tab" + (activeTab === tab.id ? " is-active" : "")}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                      {tab.id === "files" && filesCount != null && (
                        <span className="emp-tab-count">{filesCount}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* ---------------- Tab body ---------------- */}
              <div className="emp-body">
                {activeTab === "overview" && (
                  <OverviewTab
                    employeeData={emp}
                    refreshEmployeeData={() => getEmployeeData(false)}
                  />
                )}
                {activeTab === "activities" && <ActivitiesTab employeeId={emp.id ?? id} />}
                {activeTab === "resume" && <ResumeTab employeeData={emp} />}
                {activeTab === "leave" && <LeaveTab employeeId={emp.id ?? id} />}
                {activeTab === "files" && (
                  <FilesTab employeeId={emp.id ?? id} onCountChange={setFilesCount} />
                )}
              </div>
            </div>
          }
        </div>
      </div>
    </SidebarLayout>
  );
}

export default ViewEmployeePage;
